import Storage from '@aws-amplify/storage';
import { Auth } from 'aws-amplify';
import {
  importReviewVisible,
  unmappedMarkerScanVisible,
} from '../../utils/importReviewVisibility';
import {
  ImportReview,
  ImportReviewPayload,
  ImportReviewResult,
} from './import-review';
import { ImportedLabRow, ImportedRowsOutcome } from './custom-markers-table';
import { TranscribeImport } from './transcribe-import';
import {
  faExternalLinkAlt,
  faFolderOpen,
  faFolderUpload,
  faInfoCircle,
  faSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import './upload.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import React, { useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from 'react-bootstrap';

/*
 * The help page for this screen, on labsmarts.com.
 *
 * Named once because two controls open it: the glyph on the form label, which
 * opens it at the top, and "Which labs are supported?", which opens it at the
 * #supported-pdf-labs anchor. They must never drift onto two different URLs
 * for the same page.
 */
const UPLOAD_HELP_URL = 'https://labsmarts.com/upload-lab-results-help/';

/*
 * How far the Browse row runs: the left edge of Browse to the right edge of
 * "Photo, scan, or unsupported lab?", including the gap between them.
 *
 * Exported because the Client Name field on the form above is capped to it,
 * so the two rows end on the same vertical line. Both buttons are sized by
 * their own text, so this is 329px at every window width rather than a
 * fraction of anything, and it holds while the column around it grows.
 *
 * MEASURED, not computed, which is the catch: change either button's label
 * and this number is silently wrong, and the only symptom is two right edges
 * that no longer line up. Re-measure if you touch either one.
 */
export const UPLOAD_CONTROLS_WIDTH = 329;

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

/*
 * The vision read's authenticated endpoint, from lambdas/bloodworkVision.
 *
 * Unset means it is not deployed, which is why visionEnabled checks for it:
 * with no URL the picker stays single-file and a photograph is never accepted
 * at all, rather than accepted and then quietly dropped.
 */
const VISION_URL = process.env.REACT_APP_VISION_LAMBDA_URL || '';

/**
 * Why the read never came back.
 *
 * fetch() rejects a dropped connection as a bare "Failed to fetch", which reads
 * exactly like being offline and sends the practitioner to check their wifi for
 * a problem that was never theirs. The API in front of this read hangs up at
 * 30 seconds while the read itself runs for minutes on a long report, so on a
 * multi-page file that is the likely cause and it is worth naming rather than
 * blaming the network.
 */
function readLostMessage(err: any): string {
  const detail = (err && err.message) || 'connection lost';
  return (
    'The read did not come back. A report of more than a page or two currently '
    + 'takes longer than the connection allows, so the values could not be '
    + `collected (${detail}). The results already imported are unaffected.`
  );
}

/**
 * The practitioner's Cognito token, for the API in front of the read.
 *
 * The endpoint used to be a public function URL. That was fine when the thing
 * behind it was free; this one calls a model per page, so an open endpoint is
 * somebody else's bill. API Gateway rejects the call outright without this.
 */
async function authHeader(): Promise<{ [key: string]: string }> {
  const session: any = await Auth.currentSession();
  return { Authorization: `Bearer ${session.getIdToken().getJwtToken()}` };
}

/**
 * The practitioner's Cognito subject, which is the folder their uploads go in.
 *
 * The bucket's `public/` prefix is ONE namespace shared by every practitioner,
 * so an upload key carries no notion of who owns it. The vision Lambda has to
 * refuse a key belonging to somebody else — otherwise any signed-in
 * practitioner can have a stranger's lab report read aloud to them and get an
 * hour-long link to the original — and the only thing it can check the key
 * against is the subject the gateway verified on the request. So the subject
 * goes IN the key. See keyProblem() in lambdas/bloodworkVision/index.js.
 *
 * Read off the same ID token the Authorization header carries, so the two can
 * never disagree about who is calling.
 */
async function currentUserId(): Promise<string> {
  const session: any = await Auth.currentSession();
  const sub = session.getIdToken().payload?.sub;
  if (!sub) {
    throw new Error('No signed-in practitioner to file the upload under.');
  }
  return sub;
}

/**
 * Did the text parser actually get anything off this file?
 *
 * A scanned PDF — pictures in a PDF wrapper, which is what a phone's "Scan
 * Documents" produces — parses without error and yields nothing, so an empty
 * result is the signal to try reading it as a photograph rather than the signal
 * to give up.
 */
export function hasUsableResults(results: { [key: string]: any }): boolean {
  if (!results) return false;
  return Object.keys(results).some(key => {
    const value = results[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

/**
 * Did this file parse at all?
 *
 * NOT the same question as hasUsableResults, and the difference is a whole
 * report. That one asks whether any marker LabSmarts CHARTS came off the file,
 * which was the same question until the parser started handing back the rows it
 * has no field for. It is not any more: Hospital Joya issues a urinalysis whose
 * 35 rows are every one of them a custom marker, and a one-page tumor-marker
 * report whose single result is CEA. Both parse perfectly, both fill nothing
 * shipped, and both were being reported to the practitioner as a file with no
 * readable text — which sent them to re-export a PDF that was always fine.
 *
 * A row the parser could not place is still proof it read the page.
 */
export function hasUsableParse(
  results: { [key: string]: any },
  extras?: any[],
): boolean {
  return hasUsableResults(results) || (Array.isArray(extras) && extras.length > 0);
}

/**
 * Did the parser open the file and simply not know the lab?
 *
 * Exported so the rule can be tested on its own, because getting it wrong is
 * invisible: a false negative just restores the old behavior of calling an
 * unsupported lab an unreadable file, which is the bug this distinguishes.
 *
 * Deliberately narrow. Only the parser's own UNRECOGNIZED_LAB says the document
 * was read and matched nothing; a 500, a dropped connection or an S3 failure
 * are all "we broke", and telling a practitioner their file is unsupported when
 * the bucket was down would send them to re-export a file that was always fine.
 */
export function isUnrecognizedLab(ok: boolean, payload: any): boolean {
  return !ok && payload?.code === 'UNRECOGNIZED_LAB';
}

/**
 * How many names stand in the list before the rest go behind the toggle.
 *
 * A Hospital Joya panel leaves around sixty rows LabSmarts has no field for. A
 * list of sixty is not a message, it is a wall, and the practitioner only needs
 * enough of it to recognize what kind of thing was left behind.
 *
 * Six, and they are now the first six BULLETS rather than six names inside the
 * sentence. Naming them twice, once in the sentence and again at the top of the
 * list, was read as two different lists — the user's words: "you're listing them
 * in the sentence form and then the same ones in the bullet". The sentence
 * counts, the list names, and neither does the other's job.
 *
 * The toggle stays, for the second thing this list is read for: a practitioner
 * who defined a marker and MISTYPED the name reads it to find out why their row
 * stayed empty, and their row is only here if it falls in the first six.
 * `CustomMarkerRow` matching folds case and collapses runs of whitespace
 * (CustomMarker.keyFor) but never touches punctuation, so `C Peptide` against
 * Labcorp's `C-Peptide` is a miss with nothing on screen to say so.
 */
const NAMES_LISTED = 6;

/**
 * Whether the list stopped short of showing every row.
 *
 * Exported for the same reason the rules above are: it decides whether the
 * practitioner is offered the rest of the list, and a practitioner comparing
 * their own spelling against the lab's has no other way in. Reads the same
 * constant the list slices on, so the toggle cannot drift out of step with the
 * list it expands.
 */
export function hasWithheldNames(names: string[]): boolean {
  return (names || []).length > NAMES_LISTED;
}

const nameList = (names: string[]): string => {
  const listed = names.slice(0, NAMES_LISTED).join(', ');
  const rest = names.length - NAMES_LISTED;
  return rest > 0 ? `${listed}, and ${rest} more` : listed;
};

/**
 * "9 results on this report are not in My Markers:" — the line under an upload
 * introducing what the free parse could not place. The names follow it as a
 * list; this counts them.
 *
 * It USED to name the first six and close with "and 3 more", which put those
 * six on screen twice once the list underneath became bulleted. A count and a
 * colon is the whole job left: the practitioner reads the number here and the
 * names below, in one place, in the report's own order.
 *
 * Not "9 MORE results", which was the first wording. It is only true where
 * something else was imported, and a Hospital Joya urinalysis maps no shipped
 * marker at all: every row on it is one of these.
 *
 * Exported for its own tests: it is the only part of this a practitioner reads
 * word for word, and it has to stay readable at one name and at sixty.
 */
export function describeLeftovers(names: string[]): string {
  return names.length === 1
    ? 'One result on this report is not in My Markers:'
    : `${names.length} results on this report are not in My Markers:`;
}

/**
 * "LabSmarts has no field for any of the 35 results on this report." — said
 * when the import landed nothing at all, which is the one case the practitioner
 * cannot work out from an empty form.
 *
 * Said alongside the line that names the rows, not instead of it. The two
 * answer different questions on a report that landed nothing: this one says
 * why the form is empty, and the other says where those rows could go.
 */
export function describeNothingCharted(rows: number): string {
  return rows === 1
    ? 'LabSmarts has no field for the one result on this report.'
    : `LabSmarts has no field for any of the ${rows} results on this report.`;
}

/**
 * "Two results could not be read off the report: Hemoglobin, Ferritin." — the
 * rows a transcribed CSV flagged with `??` because the page was not legible.
 *
 * Only a CSV import produces these; see parsers/csvImport.js in the uploadPdf
 * Lambda for why the format has a mark for "I could not read this" at all. The
 * short version is that a model asked to read a faxed digit returns a confident
 * number rather than a doubt, so the prompt asks it to mark the character
 * instead, and a marked row is withheld rather than charted.
 *
 * Named here rather than left to the practitioner to notice, because an absent
 * row looks exactly like a row the report never had. Naming it is what makes it
 * a ten-second fix: read that one value off the page and type it.
 *
 * EVERY name is listed, where describeLeftovers stops at six and offers the
 * rest behind a toggle. The two lists are read for different reasons. That one
 * explains why a form is emptier than expected and is finished being useful
 * once the practitioner recognizes the kind of row it names. This one is a
 * to-do list, and a to-do list that stops at six quietly loses the seventh.
 */
export function describeHeld(names: string[]): string {
  return names.length === 1
    ? `One result could not be read off the report, so it was left out: ${names[0]}. Check it against the report and enter it by hand.`
    : `${names.length} results could not be read off the report, so they were left out: ${names.join(
        ', ',
      )}. Check them against the report and enter them by hand.`;
}

/**
 * The same line for a row that IS in My Markers and could not be filled anyway,
 * which is always the one mismatch worth naming: the lab printed words and the
 * marker is set up to chart a number.
 */
export function describeMismatches(names: string[]): string {
  return names.length === 1
    ? `The lab reported ${nameList(
        names,
      )} in words, and that marker is set up to chart a number.`
    : `The lab reported these in words, and those markers are set up to chart a number: ${nameList(
        names,
      )}.`;
}

/**
 * Which reader each picked file belongs to.
 *
 * Exported so the rule can be tested on its own: it decides whether a Labcorp
 * PDF reaches the parser that can read it, and getting it wrong fails quietly —
 * the wrong reader returns nothing rather than an error.
 */
export function routeUploads<T extends { type?: string; name?: string }>(
  files: T[],
): { documents: T[]; images: T[] } {
  const isImage = (f: T) =>
    /^image\//.test(f.type || '') ||
    // A file dragged from some scanners arrives with an empty MIME type.
    (!f.type && /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name || ''));
  return {
    documents: files.filter(f => !isImage(f)),
    images: files.filter(isImage),
  };
}

export const Upload = ({
  setState,
  clearForm,
  showModal,
  isUploadAvailable,
  /*
   * Present only while importReviewVisible() is on. A photographed report is
   * read by a vision model rather than parsed from a text layer, so its values
   * go through a confirmation screen before they reach the form — see
   * import-review.tsx. A Labcorp PDF still takes the setState path untouched.
   */
  onReviewImport = undefined,
  /*
   * The rows this report printed that LabSmarts has no field for, offered to
   * the practitioner's My Markers library. It fills the table itself and hands
   * back what it could not place, which is what this component puts on screen.
   *
   * Nothing to do with the vision read above and deliberately not gated with
   * it: the parse is already paid for, so placing its leftovers costs nothing.
   */
  onExtraMarkers = undefined,
}) => {
  const inputFile = useRef(null);

  const [lab] = useState('Labcorp');
  const [file, setFile] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState<ImportReviewPayload>(undefined);
  /*
   * What the free text parser just imported, kept so the practitioner can
   * choose to spend a vision read on the same file afterwards. Holding the S3
   * keys means that costs a read rather than a second upload.
   */
  const [deepScan, setDeepScan] = useState<{
    /*
     * The FILES, not the keys the free parse used. Those keys are bare
     * filenames in a namespace every practitioner shares, and the Lambda
     * refuses to read anything outside the caller's own folder — so the scan
     * re-files them under the uploader like any other vision read. One extra
     * PUT, and the ownership check keeps working.
     */
    files: File[];
    fileName: string;
    knownMarkers: string[];
    /**
     * Why the offer is being made, so it can explain itself.
     *
     * Absent means the parse succeeded and this is the ordinary "pick up what
     * the parser cannot see" offer. 'empty' is a file with no text layer, which
     * is nearly always a scan. 'unrecognized' is a document that read fine and
     * matched no lab — an unsupported lab, or not a lab report at all. Those
     * two used to be one message and they call for different things.
     */
    reason?: 'empty' | 'unrecognized';
  }>(undefined);
  const [error, setError] = useState<string>(undefined);
  /**
   * What the last upload could not place, and enough about the upload to say it
   * properly: how many markers LabSmarts charted itself, and how many rows it
   * has no field for.
   */
  // Collapsed again for every new upload: an expanded list belongs to the
  // report that produced it, not to the next one.
  const [allNamesShown, setAllNamesShown] = useState(false);
  const [leftovers, setLeftovers] = useState<{
    outcome: ImportedRowsOutcome;
    charted: number;
    extras: number;
    /** Names a transcribed CSV flagged as unreadable. See describeHeld. */
    held: string[];
  }>(undefined);
  /** The fax-and-scan screen: the prompt, and what to do with it. */
  const [transcribing, setTranscribing] = useState(false);
  /*
   * Whether the message on screen is one the transcription path answers.
   *
   * Kept beside `error` rather than inferred from its text, which would tie the
   * offer to a sentence's exact wording. Only the two failures a fax actually
   * causes set it: a file with no text in it, and a document no parser knows.
   * A dropped connection or an S3 outage must NOT offer it — the practitioner's
   * PDF was fine and sending them to transcribe it by AI is a wasted half hour.
   */
  const [offerTranscribe, setOfferTranscribe] = useState(false);
  /*
   * Guards against a stale read landing on the form. Uploading ten photographs
   * takes long enough that a practitioner can give up and pick a different set,
   * and the legacy path's filename check cannot tell two batches apart.
   */
  const requestToken = useRef(0);

  /*
   * Two gates over one reader, because they are two decisions.
   *
   * photoImportEnabled is the whole photographed-report import: the multi-file
   * picker, and routing images to the vision read. On hold.
   *
   * scanEnabled is only the button offered after a PDF has been parsed for
   * free — the one path by which a marker LabSmarts has no field for can reach
   * a test at all. Live where the gate allows it.
   */
  const photoImportEnabled =
    importReviewVisible() && !!onReviewImport && !!VISION_URL;
  const scanEnabled =
    unmappedMarkerScanVisible() && !!onReviewImport && !!VISION_URL;

  /*
   * Two ways to the same help page, and they open it in two different places
   * on purpose.
   *
   * The glyph on the "Upload Test Results" label carries no words, so the most
   * it can promise is that there is help to read about uploading a report. It
   * opens the page at the top and lets the practitioner read all of it.
   *
   * "Which labs are supported?" asks one question, so it answers that one and
   * jumps straight to the list of labs, which the page carries under that
   * anchor.
   */
  const openUploadPDFInfoWindow = () => {
    window.open(UPLOAD_HELP_URL);
  };

  const openSupportedLabsWindow = () => {
    window.open(`${UPLOAD_HELP_URL}#supported-pdf-labs`);
  };

  /*
   * Says "help" in the first word.
   *
   * The glyph alone can only promise that SOMETHING is on the other side of
   * it, and an external-link glyph beside a form label is as easily read as a
   * link out to a product page as a link to an explanation. The hover is the
   * only place that can say which, so it says it plainly rather than inviting
   * a click and leaving the reason to be discovered after the window opens.
   */
  const panelTooltip = (
    <Tooltip id="modal-tooltip">
      Help with uploading test results, including which labs are supported.
    </Tooltip>
  );

  /*
   * A PDF is parsed for free; a photograph has to be read by the vision model.
   *
   * Running every PDF through the vision reader was tried and reverted. It is
   * genuinely better at the job — markers LabSmarts has no field for,
   * qualitative results, the confirmation screen — but it costs real money per
   * report where parsing text costs nothing, and most of what it returns for a
   * Labcorp PDF is markers the parser already got right.
   *
   * So the free parser runs first and the vision read becomes something the
   * practitioner ASKS for, on the file already uploaded, when they want the
   * markers LabSmarts has no field for. See the "Scan for more markers" button.
   */
  const handleUpload = async e => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length === 0) return;
    setDeepScan(undefined);

    // Without the vision reader the picker is single-file, so this is the
    // one-PDF path running exactly as it always has.
    if (!photoImportEnabled) return handleLegacyUpload(files[0]);

    const { documents, images } = routeUploads(files);
    for (let i = 0; i < documents.length; i++) {
      await handleLegacyUpload(documents[i], { batched: files.length > 1 });
    }
    // A photograph has no free path — the parser has never been able to read
    // one — so it goes straight to the vision reader.
    if (images.length > 0) await handleVisionUpload(images);
  };

  const handleLegacyUpload = async (
    newFile,
    options: { batched?: boolean; noFallback?: boolean } = {},
  ) => {
    if (!newFile) return;

    const name = newFile.name?.replaceAll(' ', '_');
    const token = ++requestToken.current;

    setLoading(true);
    // Cleared as the new upload STARTS, not when it finishes. Without this a
    // failed parse left its red message sitting over the next file's results,
    // which reads as the new import having failed too. The same goes for what
    // the last report left unplaced.
    setError(undefined);
    setOfferTranscribe(false);
    setLeftovers(undefined);
    setAllNamesShown(false);
    setFile(newFile?.name);

    try {
      console.log('Uploading file: ', await Storage.put(name, newFile));

      const res = await fetch(
        process.env.REACT_APP_ENV === 'prod'
          ? 'https://fr3g7xel6oznoyhlvtu2ohqr6u0cbwao.lambda-url.us-east-1.on.aws/'
          : 'https://b6oxxdcpa6zsxddhvnqa7f55zy0kushi.lambda-url.us-east-1.on.aws/',
        { method: 'POST', body: JSON.stringify({ lab, name }) },
      );
      const payload = await res.json();

      /*
       * A document the parser opened and did not recognize.
       *
       * Handled before anything reads `fields`, because an error body has none
       * and destructuring it threw — which is exactly how a lab we simply have
       * no parser for came to report itself as an unreadable file. Matched on
       * the request token rather than the filename, since the token is set on
       * every call and a failure may carry no filename at all.
       */
      if (!res.ok) {
        if (token !== requestToken.current) return;
        setLoading(false);
        const unrecognized = isUnrecognizedLab(res.ok, payload);
        if (unrecognized && scanEnabled && !options.noFallback) {
          setDeepScan({
            files: [newFile],
            fileName: newFile.name,
            knownMarkers: [],
            reason: 'unrecognized',
          });
          return;
        }
        // An unrecognized document is very often a fax of a lab we DO support,
        // so the transcription path is worth offering. A plain failure is not:
        // nothing about it says the file was unreadable.
        setOfferTranscribe(unrecognized);
        setError(
          unrecognized
            ? 'That does not look like a lab report LabSmarts can read yet.'
            : 'That file could not be read.',
        );
        return;
      }

      const { fileName, results, fields } = payload;
      const collectionDate = fields?.collectionDate;

      /*
       * Is this response still about what the practitioner is waiting for?
       *
       * On its own, a single upload can be checked against the picker's value.
       * In a batch that check is useless — the input reports only the FIRST
       * file picked, so every later PDF would look like a stale response and be
       * dropped — so a batch is matched on the request token instead.
       */
      const stillWanted = options.batched
        ? token === requestToken.current
        : inputFile.current.value?.replaceAll(' ', '_')?.endsWith(fileName);

      if (!stillWanted) return;

      /*
       * The rows a transcribed CSV flagged `??`. Read before the empty-parse
       * branch because they change its answer: a CSV whose every row was held
       * charts nothing and leaves nothing over, so it would be reported as a
       * file with no readable text — both wrong and the one message that
       * cannot be acted on. Those rows ARE the read. They are what the
       * practitioner has to go and type, and naming them is the whole point.
       */
      const heldRows = (payload.held || []) as { name: string }[];
      // Transcribed by hand or by someone's AI; either way not a lab's own PDF,
      // so the scan and vision paths below have nothing to offer it.
      const isTranscribed = /\.csv$/i.test(newFile.name || '');

      /*
       * A PDF the text parser got nothing out of is very often a SCAN: a phone
       * photographed the pages and wrapped them in a PDF, so there is no text
       * layer to parse. Rather than report a failure the practitioner cannot
       * act on, read it as photographs — the file is already in the bucket, so
       * the retry costs a read, not another upload.
       */
      if (!hasUsableParse(results, payload.extras) && heldRows.length === 0) {
        /*
         * An empty CSV is a file that was written wrong, not a file that could
         * not be read, and neither reader below can do anything with it. The
         * practitioner wrote or was handed a file with no result rows in it,
         * and the fix is in the file.
         */
        if (isTranscribed) {
          setLoading(false);
          setError(
            'That CSV has no results in it. Check it has a Test Name and a Result column, and a row for each result.',
          );
          return;
        }
        /*
         * Hand it to the vision reader as page images. noFallback is what stops
         * the two readers passing the same file back and forth: the vision path
         * sets it when IT falls back to this parser.
         *
         * The file is re-uploaded rather than read from the key it already has.
         * That key is a bare filename in a namespace every practitioner shares,
         * and the vision Lambda (rightly) refuses to read anything outside the
         * caller's own folder — so the saving of one upload would cost the
         * ownership check that makes the read safe. The bytes are still in
         * memory here, so the retry is a second PUT, not a second file picker.
         */
        if (photoImportEnabled && !options.noFallback) {
          return handleVisionUpload([newFile], { fromDocument: true });
        }
        setLoading(false);
        if (scanEnabled && !options.noFallback) {
          /*
           * Offer the read rather than running it. It costs a model call per
           * page, and spending that on a practitioner's behalf because a parse
           * came back empty is not a decision to make for them.
           */
          setDeepScan({
            files: [newFile],
            fileName: newFile.name,
            knownMarkers: [],
            reason: 'empty',
          });
          return;
        }
        /*
         * What to suggest depends on the gates above. With the vision read
         * shut there is no "upload the page images" path to point at: the
         * picker is single-file, and an image handed to the text parser fails
         * as an unreadable file.
         *
         * This is the exact moment the transcription path exists for, and it
         * is why the sentence no longer ends in "enter the results by hand".
         * That was true and it was also the whole afternoon this feature was
         * built to give back. The offer goes next to the message rather than
         * in it, so the practitioner who can simply fetch a better PDF still
         * reads that as the first option.
         */
        setOfferTranscribe(true);
        setError(
          'That file has no readable text, so it is likely a scan or a photo of a printout. Upload the original PDF from the lab if you can get one.',
        );
        return;
      }

      setLoading(false);
      /*
       * A file that carries no collection date leaves the one on the form alone.
       *
       * moment(undefined) is NOW, so spreading the date unconditionally stamped
       * TODAY onto the form whenever a report did not print one, or printed one
       * no parser could read. On a single upload that is a wrong date the
       * practitioner can see and fix. The flow it actually breaks is a report
       * that arrives as several photographs and is transcribed a few pages at a
       * time: the first CSV sets the real date, and a second one with no
       * `@collected` row silently replaces it with today, because the parent
       * merges each upload into the form rather than replacing it.
       *
       * An unparseable date is treated the same as a missing one. It used to
       * reach toISOString() on an invalid moment, which returns null.
       *
       * Two moments on purpose: startOf mutates, and the UI value has always
       * kept the time of day.
       */
      const dated = moment(collectionDate);
      const datedFields =
        collectionDate && dated.isValid()
          ? {
              collectionDate: dated.startOf('day').toISOString(),
              collectionDateForUI: moment(collectionDate).toDate(),
            }
          : {};
      setState({ ...results, ...datedFields });
      /*
       * The rows no shipped marker took, offered to My Markers.
       *
       * After setState, so the handler reads a table nothing else is about to
       * rewrite, and synchronous, because what it could not place is what goes
       * on screen here. In a batch the counts are the LAST file's — a message
       * summing four reports names rows from a file the practitioner is no
       * longer looking at.
       */
      const extras = (payload.extras || []) as ImportedLabRow[];
      const placed = onExtraMarkers ? onExtraMarkers(extras) : undefined;
      const charted = Object.keys(results || {}).length;
      const worthSaying =
        placed
        && (placed.unmatched.length
          || placed.mismatched.length
          // Nothing landed anywhere. The form is empty and only this says why.
          || (!charted && !placed.filled.length && extras.length > 0));
      setAllNamesShown(false);
      const heldNames = heldRows.map(r => r.name).filter(Boolean);
      // A held row is always worth saying, whatever else the import did or did
      // not place: it is a value the report HAS and the form does not.
      setLeftovers(
        worthSaying || heldNames.length
          ? {
              outcome: placed || {
                rows: [],
                filled: [],
                unmatched: [],
                mismatched: [],
              },
              charted,
              extras: extras.length,
              held: heldNames,
            }
          : undefined,
      );
      /*
       * Offer the paid read on what the free one just handled — but never on a
       * CSV. The vision reader reads PAGES, and a CSV has none: there is no
       * image for it to look at, and whatever it made of the file would cost a
       * model call to tell the practitioner nothing. The report those rows were
       * transcribed from is the thing that could be scanned, and it is not the
       * file that was uploaded here.
       */
      if (isTranscribed) return;
      setDeepScan(prev => ({
        files: [...((prev && prev.files) || []), newFile],
        fileName: newFile.name,
        knownMarkers: [
          ...((prev && prev.knownMarkers) || []),
          ...Object.keys(results || {}),
        ],
      }));
    } catch (error) {
      console.error(error);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError('That file could not be read.');
    }
  };

  /*
   * Every page is uploaded under one folder for this batch, so two
   * practitioners photographing their own "IMG_0042.jpg" cannot overwrite each
   * other. The batch folder sits inside a folder named for the practitioner,
   * which is what lets the Lambda tell whose upload a key is before it reads it
   * or signs a URL for it — see currentUserId above.
   */
  const handleVisionUpload = async (
    files: File[],
    /**
     * This batch is a document the text parser already failed on, so a failed
     * vision read must NOT hand it back — see the fallback in
     * handleLegacyUpload. Without it the two readers take turns on one file.
     */
    options: { fromDocument?: boolean; knownMarkers?: string[] } = {},
  ) => {
    const token = ++requestToken.current;
    setLoading(true);
    setError(undefined);
    setDeepScan(undefined);
    setFile(files.length === 1 ? files[0].name : `${files.length} pages`);

    const batch = uuidv4();
    const names: string[] = [];
    try {
      const owner = await currentUserId();
      for (let i = 0; i < files.length; i++) {
        const safe = files[i].name.replace(/ /g, '_');
        const key = `bloodwork-uploads/${owner}/${batch}/${String(i).padStart(2, '0')}-${safe}`;
        await Storage.put(key, files[i], { contentType: files[i].type });
        names.push(key);
      }
    } catch (err) {
      console.error(err);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError('The upload did not finish. Check your connection and try again.');
      return;
    }
    return readAsPhotographs(
      names,
      files[0].name,
      token,
      files,
      options.knownMarkers || [],
      options,
    );
  };

  /**
   * Reads already-uploaded S3 keys as photographed pages.
   *
   * Separate from the upload so the scanned-PDF fallback can reuse it: that
   * file is already in the bucket, so the retry costs a read rather than a
   * second upload.
   */
  const readAsPhotographs = async (
    names: string[],
    fileName: string,
    token: number,
    /** The files this batch came from, so PDFs can fall back to the parser. */
    sourceFiles: File[] = [],
    /** Markers the free parser already imported, so they are not offered twice. */
    knownMarkers: string[] = [],
    /** See handleVisionUpload — stops the two readers ping-ponging one file. */
    options: { fromDocument?: boolean } = {},
  ) => {
    try {
      const res = await fetch(VISION_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({ lab, names, fileName, knownMarkers }),
      });
      const payload = await res.json();

      // The practitioner moved on; whatever came back is about a batch they
      // are no longer looking at.
      if (token !== requestToken.current) return;
      setLoading(false);

      if (!res.ok || !payload?.review?.rows?.length) {
        /*
         * Nothing came back. If every file was a document, the text parser is
         * still there and may well read it — that is the whole point of leaving
         * it in place. noFallback stops the two readers handing the file back
         * and forth.
         */
        const { documents, images } = routeUploads(sourceFiles);
        // Already been through the parser once — that is why it is here — so
        // sending it back would parse the same empty file a second time and
        // land on the same message a step later.
        if (!options.fromDocument && documents.length > 0 && images.length === 0) {
          for (let i = 0; i < documents.length; i++) {
            await handleLegacyUpload(documents[i], {
              batched: documents.length > 1,
              noFallback: true,
            });
          }
          return;
        }
        setError(
          payload?.message ||
            'That report could not be read. Try clearer photographs of each page.',
        );
        return;
      }
      setReview({ ...payload.review, fileName });
    } catch (err) {
      console.error(err);
      if (token !== requestToken.current) return;
      setLoading(false);
      setError(readLostMessage(err));
    }
  };

  /** The paid read, on a file the free parser has already been through. */
  const runDeepScan = async () => {
    if (!deepScan) return;
    // Captured first: handleVisionUpload clears the offer as it starts.
    const { files, knownMarkers } = deepScan;
    await handleVisionUpload(files, { fromDocument: true, knownMarkers });
  };

  const handleCancel = () => {
    clearForm();
    setFile(null);
    setLoading(false);
    setError(undefined);
    setDeepScan(undefined);
    /*
     * Reset with the error it belongs to, which it was not until 2026-10-12.
     *
     * Clear took the error and the offer down, both gated on `error`, and left
     * this flag true. The standing button is hidden while it is true, so the
     * practitioner was left on an empty form with no way into the
     * transcription screen at all: Clear removed the one control that answered
     * the failure they had just been told about. It reads as the feature
     * vanishing, which is exactly what it does.
     */
    setOfferTranscribe(false);
    // The leftovers line reports on a file and its values. Clear takes both
    // away, so a line still naming what that report could not place is about a
    // report the practitioner is no longer looking at, beside an empty form.
    setLeftovers(undefined);
    setAllNamesShown(false);
    requestToken.current += 1;
    inputFile.current.value = null;
  };

  const applyReview = (result: ImportReviewResult) => {
    setReview(undefined);
    onReviewImport(result);
  };

  return (
    <>
      <TranscribeImport
        show={transcribing}
        onHide={() => setTranscribing(false)}
      />
      {review && (
        <ImportReview
          payload={review}
          show={true}
          onCancel={() => {
            setReview(undefined);
            handleCancel();
          }}
          onImport={applyReview}
        />
      )}
      {/* <Form.Group as={Row} key="lab-group" controlId="lab-group">
        <Form.Label column sm>
          Lab
        </Form.Label>
        <Col sm>
          <DropdownButton id="dropdown-basic-button" title={lab}>
            <Dropdown.Item onClick={() => setLab('Labcorp')}>
              Labcorp
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setLab('Labcorp Old')}>
              Labcorp Old
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setLab('Quest')}>Quest</Dropdown.Item>
          </DropdownButton>
        </Col>
        <Col sm></Col>
      </Form.Group> */}
      {/*
       * align-items-START, not center.
       *
       * Centered, the label is centered against the WHOLE right column, and
       * that column is the tallest and most variable thing on this form: it
       * holds Browse, the questions under it, a filename, an error, the
       * leftovers note and the deep-scan offer, appearing and disappearing as
       * a practitioner works. Centering put the label 13px below Browse with
       * the form at rest and 53px below it with a leftovers note up, so the
       * one word naming the control drifted away from the control every time
       * the column grew.
       *
       * Started, it lands within a pixel of Browse's center and stays there
       * whatever else the column is showing. col-form-label's padding is what
       * does it: Bootstrap gives the label and the button the same vertical
       * padding precisely so a label and its control line up at the top of a
       * row.
       */}
      <Form.Group
        as={Row}
        key="upload-group"
        controlId="upload-group"
        className="d-flex align-items-start"
      >
        <Form.Label column sm={4}>
          {/*
           * After the words, which is the one job an external-link glyph does
           * well: it marks the words before it as something that leaves the
           * app.
           *
           * It sat after "Which labs are supported?" for a while, and there it
           * read as a second link hard against the first: two clickable things
           * a thumb apart, one of them wordless. On the label it has the whole
           * left column to itself, and the labeled link below keeps its own
           * line.
           */}
          Upload Test Results&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon
              className="upload-help-glyph external-link"
              icon={faExternalLinkAlt}
              onClick={openUploadPDFInfoWindow}
            />
          </OverlayTrigger>
        </Form.Label>
        <Col className="d-flex flex-wrap align-items-center">
          <Button
            variant="primary"
            disabled={loading}
            className="d-flex align-items-center"
            onClick={() =>
              isUploadAvailable ? inputFile.current.click() : showModal()
            }
          >
            <FontAwesomeIcon
              icon={file ? faFolderUpload : faFolderOpen}
              className="mr-3"
            />
            Browse
            <Spinner
              className="ml-3"
              animation="border"
              size="sm"
              hidden={!loading}
            />
          </Button>
          <input
            hidden
            type="file"
            ref={inputFile}
            onChange={handleUpload}
            className="form-control-file"
            /*
             * Only while the vision read is switched on. Left off, the picker
             * behaves exactly as it does in prod today: one file, no filter.
             */
            multiple={photoImportEnabled}
            /*
             * Unchanged with the gate shut: no filter, which already lets a
             * transcribed CSV be picked. Only the OPEN case needed the CSV
             * types added, because that branch does filter and would otherwise
             * grey the file out.
             *
             * Listed by extension as well as MIME because the type a CSV
             * arrives with is not dependable — Windows reports
             * `application/vnd.ms-excel` for one, and a file dragged out of
             * some mail clients has no type at all.
             */
            accept={
              photoImportEnabled
                ? 'image/*,application/pdf,text/csv,.csv'
                : undefined
            }
          />
          {/*
           * The way in for a practitioner who already KNOWS the free read
           * cannot work: the report is a picture, or it came from a lab
           * LabSmarts has no parser for.
           *
           * A BUTTON since 2026-10-12, at the user's instruction, after two
           * rounds of this control being hard to notice. It was a bare link,
           * on the reasoning that a second button beside Browse would charge
           * every practitioner a moment's attention on every visit. What that
           * reasoning kept missing is that the practitioners who need this one
           * are the ones who cannot upload at all, and a control they do not
           * recognize as a control costs them the whole feature. Outlined and
           * small, so Browse is still the filled, obvious path.
           *
           * Wears the same sparkles and the same outline as "Import it with
           * AI" below, because they open the same screen. The labels differ
           * because the contexts do: down there the failure has already been
           * named and the button only has to say what to do about it, while up
           * here the question IS the work, telling a practitioner whether this
           * is theirs to click before they have tried anything.
           *
           * NAMES THE UNSUPPORTED LAB, which is not the same kind of thing as
           * a fax and reads a little lumpy beside it. Kept anyway: both are
           * things a practitioner knows about their own file before they try,
           * and self-selection is the only job this label has. A label that
           * named only the paper shapes would leave the practitioner holding
           * an unsupported lab's PDF with no reason to click it. "Photo"
           * came off to make room, because a fax is the commonest origin and
           * the word a practitioner reaches for; the modal still lists all of
           * them.
           *
           * Stood down while that louder offer is up, which is the same room
           * reached by a second door. Two ways in, a foot apart, read as two
           * different things and send the practitioner looking for the
           * difference between them.
           */}
          {!loading && !offerTranscribe && (
            <Button
              variant="outline-primary"
              size="sm"
              className="upload-transcribe-standing"
              onClick={() => setTranscribing(true)}
            >
              <FontAwesomeIcon icon={faSparkles} className="mr-2" />
              Photo, scan, or unsupported lab?
            </Button>
          )}
          {error && (
            <b className="ml-4 text-danger">
              {error}{' '}
              <Button variant="link" onClick={handleCancel}>
                Clear
              </Button>
            </b>
          )}
          {error && offerTranscribe && (
            <div className="upload-transcribe-offer">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setTranscribing(true)}
              >
                <FontAwesomeIcon icon={faSparkles} className="mr-2" />
                Import it with AI
              </Button>
              {/*
               * "Takes a minute and costs nothing" came off on 2026-10-12.
               * Neither half held up: a report of any length takes longer than
               * a minute, and the practitioner may well be paying for the
               * assistant doing the reading. A promise about someone else's
               * product, made at the moment a practitioner is already annoyed
               * that their upload failed, is the worst possible place to be
               * wrong by a few minutes.
               */}
              <span className="upload-transcribe-note">
                Have an AI assistant transcribe the report, then upload the
                file it gives you.
              </span>
            </div>
          )}
          {!error && file && (
            <b className="ml-4">
              {file}{' '}
              <Button variant="link" onClick={handleCancel}>
                {loading ? 'Cancel' : 'Clear'}
              </Button>
            </b>
          )}
          {!error && !loading && leftovers && (
            <div className="upload-leftovers">
              {/*
               * Says what kind of message this is before any of it is read.
               *
               * A practitioner on prod read this block as a failure and stopped,
               * believing the test could not be saved, and nothing here said
               * otherwise: every line below describes something that did NOT
               * happen, and a list of markers that did not import looks exactly
               * like a list of errors. Naming the test is deliberate, because
               * "can I save this?" is the question actually being asked, and an
               * import note that does not answer it leaves the practitioner to
               * guess from the Save button.
               */}
              <div className="upload-leftovers-head">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="upload-leftovers-icon"
                />
                <span>
                  <strong>This is a note, not an error.</strong> The test can
                  still be saved.
                </span>
              </div>
              {/*
               * First of the lines below, because it is the only one that is a
               * to-do. The rest explain what the import did; this one says what
               * the practitioner still has to do by hand.
               */}
              {leftovers.held.length > 0 && (
                <div className="upload-leftover-held">
                  {describeHeld(leftovers.held)}
                </div>
              )}
              {!leftovers.charted
                && !leftovers.outcome.filled.length
                && leftovers.extras > 0 && (
                  <div>{describeNothingCharted(leftovers.extras)}</div>
                )}
              {leftovers.outcome.unmatched.length > 0 && (
                <>
                  <div>{describeLeftovers(leftovers.outcome.unmatched)}</div>
                  {/*
                   * A list, not a paragraph.
                   *
                   * These names used to run together with commas, and a comma
                   * run is prose: it is read start to finish or not at all. It
                   * is never read that way. A practitioner reads it name by
                   * name, hunting one row against a spelling in My Markers or
                   * against the report in their hand, and commas give the eye
                   * nothing to find them by.
                   *
                   * One name per line, bulleted, in as many columns as the box
                   * holds, so sixty names stay a glance instead of a paragraph
                   * and the box does not grow sixty lines tall.
                   */}
                  <ul className="upload-leftover-names">
                    {(allNamesShown
                      ? leftovers.outcome.unmatched
                      : leftovers.outcome.unmatched.slice(0, NAMES_LISTED)
                    ).map((name, i) => (
                      // Indexed, because a report can print the same name on
                      // two rows (a urinalysis prints Color twice) and the name
                      // alone is not a key.
                      <li key={`${name}-${i}`}>{name}</li>
                    ))}
                  </ul>
                  {/*
                   * Under the list rather than at the end of the sentence,
                   * which is where it sat while the sentence was the one doing
                   * the naming. It acts on the list, so it belongs at the end
                   * of the list, in the place the eye has already arrived at.
                   */}
                  {hasWithheldNames(leftovers.outcome.unmatched) && (
                    <Button
                      variant="link"
                      className="upload-leftover-toggle"
                      onClick={() => setAllNamesShown(shown => !shown)}
                    >
                      {allNamesShown
                        ? 'Show fewer'
                        : `Show all ${leftovers.outcome.unmatched.length}`}
                    </Button>
                  )}
                </>
              )}
              {leftovers.outcome.mismatched.length > 0 && (
                <div>{describeMismatches(leftovers.outcome.mismatched)}</div>
              )}
              {/*
               * Only where there is something to add. A CSV import whose rows
               * were all held has no leftovers at all, and told to add them in
               * My Markers the practitioner would go looking for rows that do
               * not exist.
               */}
              {leftovers.extras > 0
                && (leftovers.outcome.unmatched.length > 0
                  || !leftovers.outcome.filled.length) && (
                  <div>
                    Add them in My Markers, and they will import with the next
                    report.
                  </div>
                )}
            </div>
          )}
          {!error && !loading && scanEnabled && deepScan && (
            <div className="upload-deep-scan">
              <Button
                variant="outline-primary"
                size="sm"
                className="upload-deep-scan-btn"
                onClick={runDeepScan}
              >
                <FontAwesomeIcon icon={faSparkles} className="mr-2" />
                Scan for markers LabSmarts doesn&rsquo;t have
              </Button>
              <span className="upload-deep-scan-note">
                {deepScan.reason === 'empty' &&
                  'Nothing could be read from that file. If it is a scan or a photo of a printout, reading the pages with AI usually works.'}
                {deepScan.reason === 'unrecognized' &&
                  'That does not look like a lab report LabSmarts can read yet. If it is one, reading the pages with AI usually works.'}
                {!deepScan.reason &&
                  'Reads the pages with AI to pick up markers and word results the parser cannot see. Takes a moment, and costs a little.'}
              </span>
            </div>
          )}
          {/*
           * The answer to the question practitioners actually ask, in the
           * words they ask it in.
           *
           * The glyph on the form label above cannot say WHICH more there
           * is to read, and its tooltip only appears on a desktop hover, after
           * the practitioner has already decided the icon is worth
           * investigating. "Which labs are supported?" is the sentence that
           * arrives in support mail, so it is the sentence on the link, and it
           * opens the help page at the list of labs rather than the top.
           *
           * A link where its neighbor on the Browse row is a button, and the
           * difference is the point: a button does something here, a link goes
           * somewhere else. This one leaves the app.
           *
           * No glyph of its own. The only external-link glyph on this screen
           * is the one on the label, because two of them a thumb apart read as
           * two links rather than one link and one marker.
           *
           * Last in the group so that adding it moved nothing else. With the
           * form at rest it sits directly under Browse, which is what it is
           * for; when an error, a filename or an import note is up, those keep
           * the space beside Browse and this drops below them.
           *
           * Sits beside "Photo, scan, or unsupported lab?" as a pair of
           * questions, and that adjacency is the point: a practitioner who
           * checks the list and does not find their lab lands next to the one
           * control that still gets their report in.
           */}
          <div className="upload-supported-labs">
            <Button variant="link" onClick={openSupportedLabsWindow}>
              Which labs are supported?
            </Button>
          </div>
        </Col>
      </Form.Group>
    </>
  );
};
